import { ExternalApiKey, KanbanizeBoard, KanbanizeError, RepoLink, Repository } from "../types/kanbanizeTypes";

/**
 * Checks if an object is a Businessmap error.
 * @param check {any}
 * @returns boolean
 */
function isKanbanizeError(check: any): check is KanbanizeError {
	if (check && check.error && check.error.code) {
		return true;
	}
	return false;
}

function isError(check: any): check is { error: any } {
	if (check && check.error) {
		return true;
	}
	return false;
}

function isActualError(check: any): check is Error {
	if (check && check.stack && check.message) return true;
	return false;
}

// Turns a Businessmap error envelope ({ error: { code, message, reference } }) into a real Error
// so existing `catch (e) { ... e.message ... }` call sites surface the server's message instead
// of `undefined` (the raw envelope has no top-level `.message`). Falls back to a generic string
// if the server ever omits `error.message` or sends it empty, rather than producing an Error
// with an empty message.
function kanbanizeErrorToError(check: KanbanizeError): Error {
	const serverMessage = check.error && check.error.message;
	const message = serverMessage && serverMessage.trim().length > 0 ? serverMessage : "The server returned an error.";
	return new Error(message);
}

// Turns a top-level `{ error: <value> }` envelope into a real Error so it stops silently
// resolving as if it were success data. This is the shape the backend actually sends for its
// expected failures -- e.g. `{ error: "Error validating session" }` (500), `{ error: "Rate limit
// exceeded: ..." }` (429), `{ error: "Invalid Businessmap domain or API key." }` (401) -- which is
// distinct from the Businessmap envelope (`{ error: { code, message } }`, handled separately by
// isKanbanizeError/kanbanizeErrorToError). Falls back to a generic message when `error` is not a
// usable non-empty string (e.g. an object or empty string), so callers never see "[object
// Object]" or a blank message.
function topLevelErrorToError(check: { error: any }): Error {
	const raw = check.error;
	const message = typeof raw === "string" && raw.trim().length > 0 ? raw : "The server returned an error.";
	return new Error(message);
}

// Session-expiry handling (GitHub integration only). An idle tab ends up with an expired server-side
// session; the backend answers with 401 + { error, code: "SESSION_EXPIRED" }. Instead of the generic
// error alert the user is sent back to /home to log in again. Only that exact status + code pair counts:
// other 401s (bad credentials, "You have no access to board N") are legitimate and must still surface.
const SESSION_EXPIRED_CODE = "SESSION_EXPIRED";
// The login flow itself never legitimately reports an expired session, so it is left alone.
const SESSION_EXPIRY_EXEMPT_PATHS = ["/api/login", "/api/login/corporate", "/api/github/access_token", "/api/logout"];
const SESSION_EXPIRY_SKIP_PAGES = ["/home", "/login"];
// Parallel in-flight calls (LinkTable/LinkForm fire several) all see the 401; only the first redirects.
let redirecting = false;

function isGitHubBuild(): boolean {
	return process.env.REACT_APP_INTEGRATION_SERVICE === "GitHub";
}

function isSessionExpiryExemptUrl(url: string): boolean {
	try {
		const path = new URL(url, window.location.href).pathname.replace(/\/+$/, "");
		return SESSION_EXPIRY_EXEMPT_PATHS.includes(path);
	} catch {
		return false;
	}
}

// Returns true when the response is a session expiry that has been (or is being) handled by a redirect,
// in which case the caller must not surface it as an error. `status` may be undefined (tolerated as
// "not expired").
function handleSessionExpired(status: number | undefined, body: any, url: string): boolean {
	if (status !== 401 || !body || body.code !== SESSION_EXPIRED_CODE) return false;
	if (!isGitHubBuild()) return false;
	if (SESSION_EXPIRY_SKIP_PAGES.includes(window.location.pathname)) return false;
	// Mid OAuth callback the session is being established; never bounce the user away from it.
	if (new URLSearchParams(window.location.search).has("code")) return false;
	if (isSessionExpiryExemptUrl(url)) return false;
	if (!redirecting) {
		redirecting = true;
		// Must be cleared BEFORE navigating: /home redirects to /login based on a stale bm_token.
		sessionStorage.removeItem("bm_token");
		sessionStorage.removeItem("bm_domain");
		sessionStorage.removeItem("bm_apikey");
		window.location.assign("/home");
	}
	return true;
}

// Test-only: the `redirecting` flag is module-level state.
function resetSessionExpiredRedirect(): void {
	redirecting = false;
}

// For call sites that use raw fetch. If the response is a session expiry this never resolves (the page
// is navigating away), so the caller's remaining code and catch handlers do not run; otherwise it
// returns immediately and leaves the response body unread.
async function swallowIfSessionExpired(response: Response, url: string): Promise<void> {
	if (response.status !== 401) return;
	const body = await response.clone().json().catch(() => undefined);
	if (handleSessionExpired(response.status, body, url)) return new Promise<never>(() => {});
}

// Shared tail of doGet/doPost/doPut/doDelete: session-expiry detection, then body-shape error checks.
async function handleJsonResponse<T>(response: Response, url: string): Promise<T> {
	const jsonData: T = await response.json();
	if (handleSessionExpired(response.status, jsonData, url)) return new Promise<never>(() => {});
	if (isKanbanizeError(jsonData)) return Promise.reject(kanbanizeErrorToError(jsonData));
	if (isActualError(jsonData)) return Promise.reject(jsonData);
	if (isError(jsonData)) return Promise.reject(topLevelErrorToError(jsonData));
	return jsonData;
}

async function doGet<T = any>(url: string, authToken?: string): Promise<T> {
	const fetchCallOptions: any = {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
		},
	};
	if (authToken) fetchCallOptions.headers.authorization = `Bearer ${authToken}`;
	const response = await fetch(url, fetchCallOptions);
	return handleJsonResponse<T>(response, url);
}

async function doPost<T = any>(url: string, payload: any, authToken?: string, basic?: string): Promise<T> {
	const fetchCallOptions: any = {
		method: "POST",
		headers: {
			"Content-Type": basic ? "application/x-www-form-urlencoded" : "application/json",
		},
		body: basic ? payload : JSON.stringify(payload),
	};
	if (authToken) fetchCallOptions.headers.authorization = `Bearer ${authToken}`;
	//Basic is just used for oauth calls
	if (basic) fetchCallOptions.headers.authorization = `Basic ${btoa(basic)}`;

	const response = await fetch(url, fetchCallOptions);
	return handleJsonResponse<T>(response, url);
}

async function doPut<T = any>(url: string, payload: any, authToken?: string, basic?: string): Promise<T> {
	const fetchCallOptions: any = {
		method: "PUT",
		headers: {
			"Content-Type": basic ? "application/x-www-form-urlencoded" : "application/json",
		},
		body: basic ? payload : JSON.stringify(payload),
	};
	if (authToken) fetchCallOptions.headers.authorization = `Bearer ${authToken}`;
	//Basic is just used for oauth calls
	if (basic) fetchCallOptions.headers.authorization = `Basic ${btoa(basic)}`;

	const response = await fetch(url, fetchCallOptions);
	return handleJsonResponse<T>(response, url);
}

async function doDelete<T = any>(url: string, authToken?: string): Promise<T> {
	const fetchCallOptions: any = {
		method: "DELETE",
		headers: {
			"Content-Type": "application/json",
		},
	};
	if (authToken) fetchCallOptions.headers.authorization = `Bearer ${authToken}`;

	const response = await fetch(url, fetchCallOptions);
	return handleJsonResponse<T>(response, url);
}
async function getUserNameByCode(integrationDomain: string, code: string, state: string|null) {
	const integration = process.env.REACT_APP_INTEGRATION_SERVICE as string;
	const payload: { [key: string]: any } = {
		code: code,
		state: state
	};
	if (integration === "GitHub") {
		payload.client_id = process.env.REACT_APP_GH_CLIENT_ID;
	}
	if (integration === "BitBucket") payload.grant_type = "authorization_code";
	let tokenData: any;
	if (integration === "GitHub") tokenData = await doPost(`${integrationDomain}/api/github/access_token`, payload);
	if (integration === "BitBucket")
		tokenData = await doPost(
			`https://bitbucket.org/site/oauth2/access_token`,
			jsonToForm(payload),
			undefined,
			`${process.env.REACT_APP_BB_OAUTH_CLIENT_ID}:${process.env.REACT_APP_BB_OAUTH_CLIENT_SECRET}`,
		);
	let token: string = "";
	if (integration === "GitHub") return tokenData;
	if (integration === "BitBucket") token = tokenData.access_token;

	let userUrl = "";
	if (integration === "BitBucket") userUrl = "https://api.bitbucket.org/2.0/user";

	const userData = await doGet(userUrl, token);
	let userName = "";
	if (integration === "BitBucket") userName = userData.uuid;
	return userName;
}

async function populateRepos(integrationUrl: string, authToken: string) {
	let url = "";
	let repoNames: Repository[] = [];
	const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
	if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
	if (integration !== "GitHub") {
		url = `${integrationUrl}/api/${integration.toLowerCase()}/repos`;
		repoNames = await doGet(url);
	} else {
		url = `${integrationUrl}/api/repos`;
		repoNames = await doGet(url, authToken);
	}

	return repoNames;
}

const repository2SelectFormat = (repos: Repository[]) => {
	const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
	if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
	return repos.map(item => {
		if (integration === "GitLab") {
			if (!item.repo_id) throw Error("Missing id for repos");
			return { value: item.repo_id.toString(), label: item.repo_name as string };
		}
		if (integration === "GitHub") {
			return { value: item.repo_name as string, label: item.repo_name as string };
		}
		if (integration === "BitBucket") {
			return { value: item.full_name as string, label: item.full_name as string };
		}
		throw new Error(`Unrecogniced integration: ${integration}`);
	});
};

const boards2SelectFormat = (boards: KanbanizeBoard[]) => {
	return boards.map(item => {
		return { value: item.board_id.toString(), label: item.name };
	});
};

const jsonToForm = (json: { [key: string]: any }): string => {
	return Object.keys(json)
		.map(item => `${encodeURIComponent(item)}=${encodeURIComponent(json[item])}`)
		.join("&");
};

const refreshUserRepos = async (integration_domain: string) => {
	let url: string;
	if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
		url = `${integration_domain}/api/db/refresh_user_repos`;
	} else {
		url = `${integration_domain}/api/refresh_user_repos`;
	}
	return await doPost<{ savedConfigs: RepoLink[]; allRepos: Repository[] }>(url, {});
};

// PERF-H10: in-flight request de-duplication for /api/availableBoards and /api/listConfigs.
//
// On a corporate login, App.getUserName's own org-switch chain (changeCorporateUserOrg's
// setState callback) and LinkTable's constructor (getAuthorizedRepoConfig) each independently
// call refreshBoards/refreshRepolinksAndRepos for the SAME org, a beat apart, because LinkTable
// has no way to see that App's call is already in flight. That is not just wasted bandwidth: the
// backend's listConfigs handler (github-integration/src/index.ts:1199-1215) calls
// `req.session.regenerate()` + `save()`, so two concurrent listConfigs calls each regenerate the
// session - whichever `Set-Cookie` the browser applies last wins, and the other request's session
// is silently discarded. Deduping the two callers down to one real request removes that race, not
// merely the duplicate round trip.
//
// This map is keyed by (endpoint URL, currentUserOrg) and holds a promise ONLY while a request for
// that (endpoint, org) pair is in flight. The entry is deleted the instant that promise SETTLES -
// on rejection exactly as on resolution (see the `.then(evict, evict)` below) - so:
//   - two callers that overlap in time share one real network request and one outcome (success or
//     failure), and
//   - a call made after the in-flight one has settled always issues a brand new request.
// This is deliberately NOT a cache: there is no TTL and no notion of staleness, because an entry
// never outlives a single in-flight request. Do not "improve" this into a longer-lived cache (e.g.
// by widening the eviction condition or adding an expiry) - this codebase has already been bitten
// once by a memoized rejection that outlived its request (github-integration's
// RepoLinkValidationCache, PERF-L30/P92: "memoizes REJECTED promises, so one transient Businessmap
// blip fails every remaining item ... with no possibility of retry"), and a cache here would
// silently start serving stale board/repo-link data across an org switch on top of that.
//
// The org is part of the key (not just the endpoint) because a dedupe keyed on endpoint alone
// would let a request that starts during/after an org switch resolve with the PREVIOUS org's
// data - the same tenant-isolation trap as the backend's own board cache. currentUserOrg is never
// invented here: it is threaded down from the one place the app already holds it (App's own
// `currentUserOrg` state, passed as a prop to LinkTable).
const inFlightRequests = new Map<string, Promise<any>>();

function dedupeInFlightRequest<T>(endpoint: string, currentUserOrg: string, makeRequest: () => Promise<T>): Promise<T> {
	const key = `${endpoint}::org=${currentUserOrg}`;
	const existing = inFlightRequests.get(key);
	if (existing) return existing as Promise<T>;

	const request = makeRequest();
	inFlightRequests.set(key, request);

	// Runs on BOTH outcomes (the second, rejection, handler is what makes eviction happen on
	// failure too). Using `.then(evict, evict)` rather than `.finally(evict)` is deliberate: a
	// bare `.finally` re-throws the original rejection onto the promise it returns, and nothing
	// here consumes that derived promise - it would become its own, separate unhandled rejection.
	// `.then(evict, evict)` swallows it into a resolved promise instead, since neither handler
	// re-throws.
	const evict = () => {
		// Only remove the entry if it is still THIS request - guards against evicting a newer
		// request that has already taken this key after this one settled.
		if (inFlightRequests.get(key) === request) inFlightRequests.delete(key);
	};
	request.then(evict, evict);

	return request;
}

const refreshBoards = async (integration_domain: string, user_token: string | undefined, currentUserOrg: string) => {
	console.log("utils/refreshBoards: init");
	let url = "";
	if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
		url = `${integration_domain}/api/kanbanize/boards`;
	} else {
		url = `${integration_domain}/api/availableBoards`;
	}
	return dedupeInFlightRequest<KanbanizeBoard[]>(url, currentUserOrg, () => doGet(url, user_token));
};

const refreshRepolinksAndRepos = async (
	integration_domain: string,
	kanbanize_domain: string,
	user_token: string | undefined,
	currentUserOrg: string,
) => {
	const payload = {
		domain: kanbanize_domain,
	};
	let url = "";
	if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
		url = `${integration_domain}/api/db/repoLink`;
		return dedupeInFlightRequest<{ savedConfigs: RepoLink[]; allRepos: Repository[] }>(url, currentUserOrg, () => doGet(url));
	}
	url = `${integration_domain}/api/listConfigs`;
	return dedupeInFlightRequest<{ savedConfigs: RepoLink[]; allRepos: Repository[] }>(url, currentUserOrg, () =>
		doPost(url, payload, user_token),
	);
};

// External API keys (GitHub-only feature, backed by /api/ext/keys).
const listExternalApiKeys = async (integrationApiUrl: string, userToken: string): Promise<ExternalApiKey[]> => {
	return doGet<ExternalApiKey[]>(`${integrationApiUrl}/api/ext/keys`, userToken);
};

const createExternalApiKey = async (
	integrationApiUrl: string,
	userToken: string,
	name?: string,
): Promise<{ key: string }> => {
	const payload = name && name.trim().length > 0 ? { name: name.trim() } : {};
	return doPost<{ key: string }>(`${integrationApiUrl}/api/ext/keys`, payload, userToken);
};

const regenerateExternalApiKey = async (
	integrationApiUrl: string,
	userToken: string,
	name: string,
): Promise<{ key: string }> => {
	return doPut<{ key: string }>(`${integrationApiUrl}/api/ext/keys/${encodeURIComponent(name)}`, {}, userToken);
};

// Uses fetch directly (rather than the doDelete helper) because a successful call responds
// with 204 No Content, which has no body for response.json() to parse.
const deleteExternalApiKey = async (integrationApiUrl: string, userToken: string, name: string): Promise<void> => {
	const response = await fetch(`${integrationApiUrl}/api/ext/keys/${encodeURIComponent(name)}`, {
		method: "DELETE",
		headers: {
			"Content-Type": "application/json",
			authorization: `Bearer ${userToken}`,
		},
	});
	await swallowIfSessionExpired(response, `${integrationApiUrl}/api/ext/keys/${encodeURIComponent(name)}`);
	if (response.status === 204) return;
	const jsonData = await response.json();
	if (isActualError(jsonData)) return Promise.reject(jsonData);
	return Promise.reject(new Error("Unexpected response while deleting the external API key"));
};

export {
	isKanbanizeError,
	isError,
	kanbanizeErrorToError,
	topLevelErrorToError,
	doGet,
	doPost,
	doPut,
	doDelete,
	populateRepos,
	getUserNameByCode,
	isActualError,
	swallowIfSessionExpired,
	resetSessionExpiredRedirect,
	repository2SelectFormat,
	boards2SelectFormat,
	refreshUserRepos,
	refreshBoards,
	refreshRepolinksAndRepos,
	listExternalApiKeys,
	createExternalApiKey,
	regenerateExternalApiKey,
	deleteExternalApiKey,
};
